import React, { useState } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import TextField from "@material-ui/core/TextField";
import { Grid, Box, Button, Chip, InputLabel, Typography } from "@material-ui/core";
import CancelIcon from "@material-ui/icons/Cancel";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import PropTypes from "prop-types";
import _uniqueId from "lodash/uniqueId";
import { countries, getStateList } from "../constants/dropdown";
import URLS from "../constants/URL";
import LargeImageUrl from "../images/desktop.png";
import MobileImageUrl from "../images/mobile.png";
import "./EasyApplyForm.css";
import ComboSelectBox from "./ComboSelectBox";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import SimpleUploadLink from "./SimpleUploadLink";
import { prepareSaveData } from "./PrepareData";
import { requestAPI } from "./serviceUtil";
import CustomizedSnackbar from "./Snackbar";
import Loader from "./Loader";
import useMediaQuery from "@material-ui/core/useMediaQuery";

// Viewport-based sizing that matches the original pixel design at a reference viewport.
// If you want a different reference (e.g. 1920x1080), tell me and I’ll update these.
const DESIGN_WIDTH_PX = 1920;
const DESIGN_HEIGHT_PX = 910;
const vw = (px) => `calc(${px} / ${DESIGN_WIDTH_PX} * 100vw)`;
const vh = (px) => `calc(${px} / ${DESIGN_HEIGHT_PX} * 100vh)`;

// Increase overall field row width (was 686 design px)
const FIELD_LG_W = vw(686);
// Smaller gap => wider Country/State/City/Zip fields (without changing the 1-column fields)
const COL_GAP = vw(15);
const FIELD_SM_W = `calc((${FIELD_LG_W} - ${COL_GAP}) / 2)`;
const FIELD_H = vh(44);
const TOP_OFFSET = vh(150);
const FORM_BOTTOM_PAD = vh(50);
const DETAILS_SIDE_PAD = vw(60);
const ROW_GAP = vh(20); // vertical gap for wrapped 2-col groups (xs stacking)

const sourceOptions = [
  { value: "WEBSITE", title: "Website" },
  { value: "CAREER_BUILDER", title: "Career Builder" },
  { value: "DICE", title: "Dice" },
  { value: "GLASS_DOOR", title: "Glass Door" },
  { value: "INDEED", title: "Indeed" },
  { value: "LINKEDIN", title: "LinkedIn" },
  { value: "MONSTER", title: "Monster" },
  { value: "STACK_OVERFLOW", title: "Stack Overflow" },
  { value: "ZIPRECRUITER", title: "Ziprecruiter" },
  { value: "INTERNAL", title: "Internal" },
  { value: "EMAIL_MARKETING", title: "Email Marketing" },
];

const styles = {
  lgImage: {
    width: "100%",
    height: "100vh",
    objectFit: "cover",
  },
  mobileImage: {
    width: "100%",
    height: "auto",
  },
  formStyle: {
    marginTop: TOP_OFFSET,
    paddingBottom: FORM_BOTTOM_PAD,
    width: FIELD_LG_W,
    maxWidth: "100%",
    marginLeft: "auto",
    marginRight: "auto",
  },
  mobileForm: {
    marginTop: 0,
    // Keep mobile layout comfortable (close to original), while avoiding px.
    padding: `5% 5% ${vh(50)}`,
    maxWidth: "100%",
    marginLeft: "auto",
    marginRight: "auto",
  },
  bgDescription: {
    backgroundColor: "#fff",
    width: "100%",
    minHeight: "100vh",
  },
  reqDetailsHeader: {
    fontSize: vh(16),
    fontWeight: "bold",
    fontFamily: "'Montserrat', sans-serif",
    marginTop: 0,
    marginBottom: "1.8vh",
  },
  reqDetailsText: {
    fontSize: vh(16),
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: "bold",
    margin: "1vh 0",
  },
  reqDetailsValue: {
    fontWeight: "normal",
    color: "#333333",
  },
  verticalLine: {
    position: "absolute",
    left: 0,
    top: "50%",
    height: vh(662),
    width: "0.15vh",
    backgroundColor: "#C0C0C0",
    zIndex: 1,
    transform: "translateY(-50%)",
  },
  labelText: {
    color: "#000",
    fontSize: vh(16),
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 500,
    marginBottom: "0.5vh",
    marginTop: "2.5vh",
  },
  textFieldBg: {
    backgroundColor: "#fff",
  },
  outlinedBorder: {
    fontFamily: "'Montserrat', sans-serif",
    background: "#fff",
    borderRadius: "0.8vh",
  },
  fieldLarge: {
    width: FIELD_LG_W,
    maxWidth: "100%",
  },
  fieldSmall: {
    width: FIELD_SM_W,
    maxWidth: "100%",
  },
  inputProps: {
    style: {
      padding: `0 ${vh(14)}`,
      height: FIELD_H,
      fontSize: vh(16),
      fontFamily: "'Montserrat', sans-serif",
    },
  },
};

const validation = (regex, value) => {
  if (value === "" || regex.test(value.trim())) {
    return true;
  }
  return false;
};
const stringValidation = (value) => {
  return validation(/^[ a-zA-Z]+$/, value);
};
const numberValidation = (value) => {
  return validation(/^[0-9]{1,10}$/g, value);
};
const cityValidation = (value) => {
  return validation(/^[ A-Za-z-,.']+$/, value);
};
const zipValidation = (value) => {
  return validation(/^[ A-Za-z0-9]{1,8}$/g, value);
};
const checkLength = (value) => {
  return !value ? true : (value + "").length >= 5 && (value + "").length <= 8;
};

const EasyApplyForm = (props) => {
  const [state, setState] = useState({
    initialValue: {
      fullName: "",
      email: "",
      phoneCountryCode: "1",
      phone: "",
      country: "",
      state: "",
      city: "",
      zip: "",
      source: "",
      candidate_resume: [],
      requisitionNumber: "",
      positionTitle: "",
      action: "SUBMIT",
    },
    isShowLoading: false,
    stateName: "",
    file: "",
    resumeValidation: false,
  });
  const [isAppliedSuccess, setIsAppliedSuccess] = useState(false);
  const [showSnack, setShowSnack] = useState({});
  const showSnackMessage = (type, message) => {
    const id = _uniqueId("prefix-");
    const obj = {
      type,
      message,
      snackOpen: id,
    };
    setShowSnack(obj);
  };

  const validationSchema = Yup.object({
    fullName: Yup.string().required(" "),
    email: Yup.string().email("Enter a valid email").required(" "),
    phone: Yup.string()
      .required(" ")
      .length(10, "Phone number must be 10 digit"),
    country: Yup.string().required(" "),
    state: Yup.string().when("country", {
      is: (val) => val !== "OTHER",
      then: Yup.string().required(" "),
      otherwise: Yup.string(),
    }),
    city: Yup.string().required(" "),
    zip: Yup.string()
      .required(" ")
      .test("len", "Zip must be 5-8 characters", (value) => checkLength(value)),
  });

  const params = new Proxy(new URLSearchParams(window.location.search), {
    get: (searchParams, prop) => searchParams.get(prop),
  });
  let qrid = params.rid;
  let qjt = params.jt;


  const saveApplication = async (data) => {
    setState({
      ...state,
      isShowLoading: true,
    });
    data.candidate_resume = state.file;
    data.requisitionNumber = qrid === null || qrid === undefined ? "" : qrid;
    data.positionTitle = qjt === null || qjt === undefined ? "" : qjt;
    const formData = prepareSaveData(data);
    const response = await requestAPI(
      URLS.createCandidate,
      formData,
      "POST",
      "createCandidate"
    );
    if (response.message === "SUCCESS - SUBMIT") {
      setState({
        ...state,
        isShowLoading: false,
      });
      setIsAppliedSuccess(true);
    } else {
      setState({
        ...state,
        isShowLoading: false,
      });
      showSnackMessage("error", "Something went wrong. Please try again.");
    }
  };
  const uploadResume = async (file) => {
    setState((prev) => ({
      ...prev,
      isShowLoading: true,
    }));
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await requestAPI(
        URLS.uploadResume,
        formData,
        "POST",
        "uploadFile"
      );
      if (response && response.message === "SUCCESS") {
        showSnackMessage("success", "File uploaded successfully!");
      } else {
        showSnackMessage("error", "Something went wrong. Please try again.");
      }
    } finally {
      setState((prevState) => ({
        ...prevState,
        isShowLoading: false,
      }));
    }
  };
  const handleFileUpload = async ({ target }) => {
    const { name, files } = target;
    if (!files || !files.length) return;

    var fileList = [];
    for (var i = 0; i < files.length; i++) {
      fileList.push(files[i]);
    }
    setState((prev) => ({
      ...prev,
      file: fileList,
    }));

    if (name === "candidate_resume") {
      await uploadResume(files[0]);
    }
  };
  function getStateListAll(selectedCountry) {
    const stateList = getStateList(selectedCountry);                                                      
    setState((prevState) => ({
      ...prevState,
      initialValue: {
        ...prevState.initialValue,
        country: selectedCountry,
        state: "",
      },
      stateName: stateList,
    }));
  }
  const isMobileDevice = useMediaQuery("(max-width:599px)");
  const isTabletOrLaptopDevice = useMediaQuery("(min-width:600px)");
  return (
    <Grid container>
      <Grid
        item
        xs={12}
        sm={3}
        style={{
          height: isTabletOrLaptopDevice ? "100vh" : "auto",
          display: isTabletOrLaptopDevice ? "flex" : "block",
          position: isTabletOrLaptopDevice ? "sticky" : "static",
          top: 0,
        }}
      >
        <Box
          component="img"
          style={isTabletOrLaptopDevice ? styles.lgImage : styles.mobileImage}
          alt="Easy Apply User Interface"
          src={isTabletOrLaptopDevice ? LargeImageUrl : MobileImageUrl}
        />
      </Grid>
      {isMobileDevice && (
        <Grid item xs={12}>
          <div style={{ backgroundColor: "#fff" }}>
            <div style={{ padding: "4vh 5vw" }}>
              <p style={styles.reqDetailsHeader}>
                Requisition Details
              </p>
              <p style={styles.reqDetailsText}>
                Requisition Number: <span style={styles.reqDetailsValue}>{qrid}</span>
              </p>
              <p style={{ ...styles.reqDetailsText, marginBottom: 0 }}>
                Position Title: <span style={styles.reqDetailsValue}>{qjt}</span>
              </p>
            </div>
          </div>
        </Grid>
      )}
      <Grid item xs={12} sm={9}>
        {!isAppliedSuccess ? (
          <Formik
            initialValues={state.initialValue}
            validationSchema={validationSchema}
            onSubmit={(values) => {
              state.file
                ? saveApplication(values)
                : setState({ ...state, resumeValidation: true });
            }}
          >
            {({ errors, touched, handleSubmit, handleChange, values }) => (
              <>
                {/*
                  Mobile should stay simple: full-width inputs and stacked rows.
                  Desktop/tablet keeps the design-sized vw/vh layout.
                */}
                <form id="easy-apply-form" onSubmit={handleSubmit}>
                  {state.isShowLoading && <Loader />}
                  <Grid container>
                    <Grid item xs={12} sm={7}>
                      {/*
                        Field sizing helpers:
                        - Desktop/tablet: design widths (FIELD_LG_W / FIELD_SM_W)
                        - Mobile: full width
                      */}
                      {(() => {
                        const fieldLargeStyle = isTabletOrLaptopDevice
                          ? styles.fieldLarge
                          : { width: "100%" };
                        const fieldSmallStyle = isTabletOrLaptopDevice
                          ? styles.fieldSmall
                          : { width: "100%" };
                        const rowWidthStyle = {
                          width: isTabletOrLaptopDevice ? FIELD_LG_W : "100%",
                          maxWidth: "100%",
                        };
                        const columnGap = isTabletOrLaptopDevice ? COL_GAP : 0;
                        const rowGap = isTabletOrLaptopDevice ? ROW_GAP : vh(16);

                        return (
                          <div
                            style={
                              isTabletOrLaptopDevice
                                ? styles.formStyle
                                : styles.mobileForm
                            }
                          >
                            <Grid item xs={12} sm={12}>
                              <InputLabel
                                htmlFor="fullName"
                                style={styles.labelText}
                              >
                                Full Name
                              </InputLabel>
                              <TextField
                                id="fullName"
                                name="fullName"
                                variant="outlined"
                                placeholder="Enter Your Full Name"
                                value={values.fullName}
                                onChange={(e) => {
                                  e.preventDefault();
                                  if (stringValidation(e.target.value))
                                    handleChange(e);
                                }}
                                inputProps={styles.inputProps}
                                helperText={touched.fullName ? errors.fullName : ""}
                                error={touched.fullName && Boolean(errors.fullName)}
                                margin="none"
                                style={{ ...styles.textFieldBg, ...fieldLargeStyle }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={12}>
                              <InputLabel htmlFor="email" style={styles.labelText}>
                                Email
                              </InputLabel>
                              <TextField
                                id="email"
                                name="email"
                                value={values.email}
                                placeholder="eg.petegabiuser123@outlook.com"
                                onChange={handleChange}
                                variant="outlined"
                                inputProps={styles.inputProps}
                                helperText={touched.email ? errors.email : ""}
                                error={touched.email && Boolean(errors.email)}
                                margin="none"
                                style={{ ...styles.textFieldBg, ...fieldLargeStyle }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={12}>
                              <Grid
                                container
                                style={rowWidthStyle}
                                spacing={0}
                                columnGap={columnGap}
                                rowGap={rowGap}
                              >
                                <Grid item xs={12} sm={6}>
                                  <InputLabel htmlFor="phone" style={styles.labelText}>
                                    Phone Number
                                  </InputLabel>
                                  <PhoneInput
                                    country={"us"}
                                    value={values.phoneCountryCode + values.phone}
                                    onChange={(value, countryData) => {
                                      const dialCode = countryData.dialCode || "";
                                      const phoneNumber = value.slice(dialCode.length);
                                      values.phoneCountryCode = dialCode;
                                      values.phone = phoneNumber;
                                    }}
                                    inputProps={{
                                      name: "phone",
                                      id: "phone",
                                    }}
                                    containerStyle={{
                                      marginTop: 0,
                                      width: fieldSmallStyle.width || "100%",
                                      maxWidth: "100%",
                                      height: FIELD_H,
                                    }}
                                    inputStyle={{
                                      width: "100%",
                                      height: "100%",
                                      backgroundColor: "#fff",
                                      borderRadius: 7,
                                      fontSize: vh(16),
                                      fontFamily: "'Montserrat', sans-serif",
                                    }}
                                    buttonStyle={{
                                      backgroundColor: "#fff",
                                      borderRadius: "7px 0 0 7px",
                                      height: "100%",
                                    }}
                                    enableSearch={true}
                                    searchPlaceholder="Search country..."
                                  />
                                  {touched.phone && errors.phone && (
                                    <p style={{ color: "#f44336", fontSize: "0.75rem", margin: "3px 14px 0" }}>
                                      {errors.phone}
                                    </p>
                                  )}
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <InputLabel htmlFor="country" style={styles.labelText}>
                                    Country
                                  </InputLabel>
                                  <ComboSelectBox
                                    name="country"
                                    id="country"
                                    options={countries}
                                    displayLabel="Your Country"
                                    onChange={(e) => {
                                      getStateListAll(e.target.value);
                                    }}
                                    style={{
                                      ...fieldSmallStyle,
                                      height: FIELD_H,
                                      maxWidth: "100%",
                                    }}
                                  />
                                </Grid>
                              </Grid>
                            </Grid>

                            <Grid item xs={12} sm={12}>
                              <Grid
                                container
                                style={rowWidthStyle}
                                spacing={0}
                                columnGap={columnGap}
                                rowGap={rowGap}
                              >
                                <Grid item xs={12} sm={6}>
                                  <InputLabel htmlFor="state" style={styles.labelText}>
                                    State
                                  </InputLabel>
                                  <ComboSelectBox
                                    name="state"
                                    id="state"
                                    options={state.stateName || []}
                                    displayLabel="Your State"
                                    disabled={values.country === "OTHER"}
                                    style={{
                                      ...fieldSmallStyle,
                                      height: FIELD_H,
                                      maxWidth: "100%",
                                    }}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <InputLabel htmlFor="city" style={styles.labelText}>
                                    City
                                  </InputLabel>
                                  <TextField
                                    id="city"
                                    name="city"
                                    placeholder="Your City"
                                    value={values.city}
                                    onChange={(e) => {
                                      e.preventDefault();
                                      if (cityValidation(e.target.value))
                                        handleChange(e);
                                    }}
                                    variant="outlined"
                                    inputProps={styles.inputProps}
                                    helperText={touched.city ? errors.city : ""}
                                    error={touched.city && Boolean(errors.city)}
                                    margin="none"
                                    style={{
                                      ...styles.textFieldBg,
                                      ...fieldSmallStyle,
                                      height: FIELD_H,
                                      maxWidth: "100%",
                                    }}
                                  />
                                </Grid>
                              </Grid>
                            </Grid>

                            <Grid item xs={12} sm={12}>
                              <Grid
                                container
                                style={rowWidthStyle}
                                spacing={0}
                                columnGap={columnGap}
                                rowGap={rowGap}
                              >
                                <Grid item xs={12} sm={6}>
                                  <InputLabel htmlFor="zip" style={styles.labelText}>
                                    Zipcode
                                  </InputLabel>
                                  <TextField
                                    id="zip"
                                    name="zip"
                                    placeholder="Zipcode"
                                    value={values.zip}
                                    maxLength="8"
                                    onChange={(e) => {
                                      e.preventDefault();
                                      if (zipValidation(e.target.value))
                                        handleChange(e);
                                    }}
                                    variant="outlined"
                                    inputProps={styles.inputProps}
                                    helperText={touched.zip ? errors.zip : ""}
                                    error={touched.zip && Boolean(errors.zip)}
                                    margin="none"
                                    style={{
                                      ...styles.textFieldBg,
                                      ...fieldSmallStyle,
                                      height: FIELD_H,
                                      maxWidth: "100%",
                                    }}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <InputLabel htmlFor="source" style={styles.labelText}>
                                    Source
                                  </InputLabel>
                                  <ComboSelectBox
                                    name="source"
                                    id="source"
                                    options={sourceOptions}
                                    displayLabel="Source"
                                    style={{
                                      ...fieldSmallStyle,
                                      height: FIELD_H,
                                      maxWidth: "100%",
                                    }}
                                  />
                                </Grid>
                              </Grid>
                            </Grid>

                            <Grid
                              item
                              xs={12}
                              style={{
                                marginTop: "3vh",
                                textAlign: "center",
                              }}
                            >
                              <Typography
                                variant="body2"
                                style={{
                                  fontFamily: "'Montserrat', sans-serif",
                                  fontSize: isTabletOrLaptopDevice ? vh(13) : 12,
                                  color: "#555",
                                  lineHeight: 1.6,
                                }}
                              >
                                By submitting, I have read and agree to the{" "}
                                <a
                                  href="https://www.ptechpartners.com/privacy-policy-2/"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ color: "#1a337e", textDecoration: "underline" }}
                                >
                                  Privacy Policy
                                </a>{" "}
                                &amp;{" "}
                                <a
                                  href="https://www.ptechpartners.com/terms-and-conditions/"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ color: "#1a337e", textDecoration: "underline" }}
                                >
                                  Terms &amp; Conditions
                                </a>
                              </Typography>
                            </Grid>

                            <Grid
                              item
                              style={{
                                marginTop: "2vh",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexDirection: isMobileDevice ? "column" : "row",
                                rowGap: isMobileDevice ? vh(16) : undefined,
                              }}
                            >
                              <Box>
                                <SimpleUploadLink
                                  onChange={handleFileUpload}
                                  labelText="Upload Resume"
                                  name="candidate_resume"
                                  id="candidate_resume"
                                  accept=".doc,.docx,.ppt,.pdf"
                                  value={""}
                                />
                                {state.resumeValidation && !state.file && (
                                  <p
                                    style={{
                                      margin: 0,
                                      marginTop: "1vh",
                                      color: "#B53E4E",
                                      fontSize: "clamp(1.55vh, 0.9vw, 1.85vh)",
                                      textAlign: "center"
                                    }}
                                  >
                                    Please upload the resume
                                  </p>
                                )}
                              </Box>
                              <Button
                                variant="contained"
                                type="submit"
                                style={{
                                  width: isMobileDevice ? "90vw" : vw(160),
                                  maxWidth: "100%",
                                  height: "4.8vh",
                                  borderRadius: "3vh",
                                  fontSize: "clamp(1.4vh, 0.85vw, 1.7vh)",
                                  color: "#FFF",
                                  background: "#1a337e",
                                  fontWeight: 500,
                                  fontFamily: "'Montserrat', sans-serif",
                                  textTransform: "none",
                                  marginLeft: isMobileDevice ? 0 : "2vw",
                                }}
                                onClick={() => {
                                  if (!state.file) {
                                    setState({ ...state, resumeValidation: true });
                                  }
                                }}
                              >
                                Submit
                              </Button>
                            </Grid>

                            <Grid item style={{ textAlign: "center", width: "100%" }}>
                              {state.file && state.file.length > 0
                                ? state.file.map((i) => (
                                    <Chip
                                      key={"candidate_resume_chip" + i.name}
                                      size="medium"
                                      label={i.name}
                                      style={{ marginTop: "1.2vh" }}
                                      onDelete={() => {
                                        setState({ ...state, file: [] });
                                      }}
                                      deleteIcon={
                                        <CancelIcon style={{ color: "#1a337e" }} />
                                      }
                                    />
                                  ))
                                : null}
                            </Grid>
                          </div>
                        );
                      })()}
                    </Grid>
                    {isTabletOrLaptopDevice && (
                      <Grid
                        item
                        xs={12}
                        sm={5}
                        style={{ position: "relative", minHeight: "100vh" }}
                      >
                        <div style={styles.verticalLine} />
                        <div style={styles.bgDescription}>
                          <div style={{ padding: `${TOP_OFFSET} ${DETAILS_SIDE_PAD} 0`, maxWidth: "40vw" }}>
                            <p style={styles.reqDetailsHeader}>
                              Requisition Details
                            </p>
                            <p style={styles.reqDetailsText}>
                              Requisition Number: <span style={styles.reqDetailsValue}>{qrid}</span>
                            </p>
                            <p style={styles.reqDetailsText}>
                              Position Title: <span style={styles.reqDetailsValue}>{qjt}</span>
                            </p>
                          </div>
                        </div>
                      </Grid>
                    )}
                  </Grid>
                </form>
              </>
            )}
          </Formik>
        ) : (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            style={{
              minHeight: isTabletOrLaptopDevice ? "100vh" : "auto",
              padding: isTabletOrLaptopDevice ? "0 10%" : "10% 5%",
              textAlign: "center",
            }}
          >
            <CheckCircleOutlineIcon
              style={{
                fontSize: isTabletOrLaptopDevice ? "8vh" : 60,
                color: "#1a337e",
                marginBottom: "2vh",
              }}
            />
            <Typography
              variant="h4"
              style={{
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 700,
                color: "#1a337e",
                marginBottom: "2vh",
                fontSize: isTabletOrLaptopDevice ? vh(28) : 22,
              }}
            >
              Thank You for Applying!
            </Typography>
            <Typography
              variant="body1"
              style={{
                fontFamily: "'Montserrat', sans-serif",
                color: "#333",
                fontSize: isTabletOrLaptopDevice ? vh(16) : 14,
                maxWidth: 600,
                lineHeight: 1.8,
              }}
            >
              We've received your application and our team is reviewing it. If your
              resume is shortlisted, our AI assistant will reach out for a
              pre-qualification call to verify your details and discuss the role.
            </Typography>
          </Box>
        )}
      </Grid>
      {showSnack.type ? <CustomizedSnackbar alertMsg={showSnack} /> : null}
    </Grid>
  );
};

EasyApplyForm.propTypes = {
  resumeValidation: PropTypes.bool,
};

export default EasyApplyForm;
